import React, { useState, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import getStyles from 'components/Catalog/ProductGrid/styles';
import ProductTile from 'components/Catalog/ProductGrid/ProductTile';
import LazyLoad from 'components/LazyLoad';
import {
    Box, Flex, Text, Button, Grid, Container
} from 'components/ui';
import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';
import { createVisibilityTracker, trackProductView, trackProductClick } from 'components/CreatorStoreFront/helpers/csfEventHelpers';
import { updateAttributionForDetailsPage, updateAttributionWithProductId } from 'components/CreatorStoreFront/helpers/csfAttribution';
import { getCsfRoute } from 'components/CreatorStoreFront/helpers/getCsfRoute';
import { applyProductInteractionToActiveCommissioningU1, isDynamicCommissioningEnabled } from 'utils/CreatorStoreFront/dynamicCommissioning';

const SERVER_RENDER_LIMIT = 60;

function CSFProductGrid(props) {
    const {
        handle,
        pageType,
        collectionId,
        fetchProductData,
        products,
        source,
        textResources,
        creatorProfileData,
        totalProductCount,
        postId,
        getText
    } = props;

    // State
    const [pageNum, setPageNum] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    // Refs
    const observerRef = useRef(null);
    const rootRef = useRef(null);

    // fetchMoreProducts function
    const fetchMoreProducts = async nextPageNum => {
        const args = pageType === CSF_PAGE_TYPES.COLLECTION ? [collectionId, handle, nextPageNum] : [handle, nextPageNum];

        setIsLoading(true);

        try {
            await fetchProductData(...args);
            setPageNum(nextPageNum);
        } catch (error) {
            Sephora.logger.error(`Error during fetching ${pageType} page ${nextPageNum}:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    // handleLoadMore function
    const handleLoadMore = () => {
        const nextPage = pageNum + 1;
        fetchMoreProducts(nextPage);
    };

    // renderProducts function
    const renderProducts = () => {
        const addToBasketText = textResources?.addToBasket || getText('addToBasket');
        const styles = getStyles();
        const creatorProfile = creatorProfileData?.creatorProfile;
        const referralOwnerId = creatorProfile?.creatorId;

        return products.map((product, index) => {
            const isLazyLoaded = index >= SERVER_RENDER_LIMIT;
            const Component = isLazyLoaded ? LazyLoad : ProductTile;
            const itemId = product?.productId || index;
            const motomProductId = product?.motomProductId;

            const trackCSFProductClick = () => {
                trackProductClick({
                    referralOwnerId,
                    productId: itemId,
                    targetUrl: product?.targetUrl,
                    motomProductId,
                    collectionId
                });
            };

            const updateAttributionData = () => {
                const isCollectionDetailsView = pageType === CSF_PAGE_TYPES.COLLECTION;

                if (isCollectionDetailsView) {
                    updateAttributionForDetailsPage(creatorProfile, motomProductId);
                } else {
                    updateAttributionWithProductId(motomProductId);
                }

                // Mirror the legacy update on the DC active/current U1 state
                // per the U1 Parameter Analytics Training Guide Rule 3.
                // Flag-guarded — no-op when DC is off so legacy behavior is
                // unchanged. Reads section/identifier from the current URL
                // (rather than `pageType`) so detail-page identifiers are
                // captured for the `cxxc` / `pxxc` unique-key branch.
                if (isDynamicCommissioningEnabled()) {
                    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
                    const { section = '', identifier = null } = getCsfRoute(pathname);

                    // CSF-1791: async (re-fetches siteId for the new u1).
                    // Fire-and-forget — the destination page reuses the
                    // persisted `{ u1, siteId }` pair on its page_view, and
                    // the helper swallows fetch errors internally so the
                    // returned promise resolves rather than rejects.
                    applyProductInteractionToActiveCommissioningU1({ section, identifier, motomProductId });
                }
            };

            return (
                <div
                    key={`csfProductGridItem_${itemId}`}
                    css={[styles.item, isLazyLoaded && styles.itemLazy]}
                    style={{ order: index }}
                    data-tracking-id={itemId}
                    data-motom-tracking-id={motomProductId}
                >
                    <Component
                        {...(isLazyLoaded && {
                            isLazyLoaded: true,
                            component: ProductTile
                        })}
                        source={source}
                        product={product}
                        isSkeleton={!product}
                        index={index}
                        displayAddToBasketCta={true}
                        showVariationValue={true}
                        customMarginTop={1}
                        addToBasket={addToBasketText}
                        customStyle={{ width: 110 }}
                        trackCSFProductClick={trackCSFProductClick}
                        updateAttributionData={updateAttributionData}
                    />
                </div>
            );
        });
    };

    // setProductTilesVisibilityTracking function
    const setProductTilesVisibilityTracking = () => {
        const referralOwnerId = creatorProfileData?.creatorProfile?.creatorId;

        // Add visibility tracking for products
        if (!isLoading && products.length > 0) {
            // Clean up previous observer
            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            // Setup new intersection observer for product tracking
            observerRef.current = createVisibilityTracker({
                onVisible: (productId, element) => {
                    const motomProductId = element?.getAttribute('data-motom-tracking-id');

                    trackProductView({
                        referralOwnerId,
                        productId,
                        postId,
                        collectionId,
                        motomProductId
                    });
                },
                threshold: 0.5, // This is per jira spec
                trackOnce: false // Allow re-tracking for product views - needed for csf product tile click sot event
            });

            // Small delay to ensure DOM is updated after render
            setTimeout(() => {
                const tiles = document.querySelectorAll('[data-tracking-id]');
                tiles.forEach(tile => {
                    const productId = tile.getAttribute('data-tracking-id');

                    if (productId) {
                        // Set data-tracking-id for the visibility tracker
                        tile.setAttribute('data-tracking-id', productId);
                        observerRef.current.observe(tile);
                    }
                });
            }, 100);
        }
    };

    // renderResultsText function (from parent ProductGrid)
    const renderResultsText = ({ resultsText }) => {
        return (
            <Text
                is='p'
                lineHeight='tight'
                color='gray'
                fontSize={['sm', 'base']}
            >
                {resultsText}
            </Text>
        );
    };

    // componentDidMount - setup visibility tracking
    useEffect(() => {
        setProductTilesVisibilityTracking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidUpdate - handle products changes
    useEffect(() => {
        setProductTilesVisibilityTracking();
    }, [products.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // componentWillUnmount - cleanup observer
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Render logic
    const count = products?.length;
    const styles = getStyles();

    const totalLoadedProducts = `${count} ${getText('of')} ${totalProductCount} ${getText(totalProductCount === 1 ? 'product' : 'products')}`;
    const totalItems = `${totalProductCount} ${getText(totalProductCount === 1 ? 'item' : 'items')}`;

    // Determine if we're inside a collection details view
    const isCollectionDetailsView = pageType === CSF_PAGE_TYPES.COLLECTION;

    const isShowMoreCentered = products?.length % 4 === 0;
    const isShowMoreItemsTile = count < totalProductCount;

    return (
        <Container ref={rootRef}>
            {/* Only show heading and grid if not in collection details view */}
            {!isCollectionDetailsView && (
                <>
                    <Text
                        is={'h2'}
                        fontSize={['md', null, 'lg']}
                        fontWeight={'bold'}
                        lineHeight={['20px', null, '22px']}
                        marginBottom={[1, null, 2]}
                        children={textResources.allProducts}
                    />
                    <Grid
                        gap={[2, null, 3]}
                        marginBottom={[3, null, 5]}
                    >
                        {renderResultsText({
                            resultsText: totalItems
                        })}
                    </Grid>
                </>
            )}
            {count > 0 ? (
                <div
                    css={styles.root}
                    id='prod-grid-csf'
                >
                    {renderProducts()}

                    {/* show more items tile */}
                    {isShowMoreItemsTile && (
                        <Flex
                            flexDirection={'column'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            gap={'10px'}
                            width={['100%', null, 240]}
                            paddingTop={[2, null, 4]}
                            paddingBottom={[2, null, 3]}
                            flex={['none', null, isShowMoreCentered ? 'auto' : 'none']}
                            order={9999}
                        >
                            <Text
                                is='p'
                                color='gray'
                                lineHeight='tight'
                                fontSize={['sm', 'base']}
                                children={totalLoadedProducts}
                            />
                            <Button
                                disabled={isLoading}
                                variant='secondary'
                                width={200}
                                onClick={handleLoadMore}
                                children={getText('showMoreProducts')}
                            />
                        </Flex>
                    )}
                </div>
            ) : (
                <Box lineHeight='tight'>
                    <Text
                        is='h2'
                        fontWeight='bold'
                        fontSize={['md', 'lg']}
                        marginBottom='.25em'
                        children={getText('noResults1')}
                    />
                    <Text
                        is='p'
                        fontSize={[null, 'md']}
                        css={{ whiteSpace: 'normal' }}
                        children={getText('noResults2')}
                    />
                </Box>
            )}
        </Container>
    );
}

export default wrapFunctionalComponent(CSFProductGrid, 'CSFProductGrid');
